/**
 * MuteButton — draws a pixel-art speaker icon in the bottom-right of any
 * Phaser scene. Clicking toggles music mute via MusicManager.
 *
 * Usage: import { addMuteButton } from './MuteButton';
 *        addMuteButton(this);  // in scene create()
 */
import MusicManager from "./MusicManager";

const SIZE = 20; // icon area
const INSET_X = 25; // from the right edge
const INSET_Y = 20; // from the bottom edge

/**
 * Draw the speaker icon + optional sound waves or X mark.
 */
function drawIcon(gfx, muted, X, Y) {
  gfx.clear();

  // Speaker body — small trapezoid shape
  gfx.fillStyle(muted ? 0x555555 : 0x00ffff, muted ? 0.5 : 0.8);

  // Speaker cone (left rectangle)
  gfx.fillRect(X - 14, Y - 4, 6, 8);
  // Speaker bell (right triangle-ish)
  gfx.fillTriangle(X - 8, Y - 4, X - 8, Y + 4, X - 2, Y - 8);
  gfx.fillTriangle(X - 8, Y + 4, X - 2, Y - 8, X - 2, Y + 8);

  if (muted) {
    // X mark
    gfx.lineStyle(2, 0xff4444, 0.8);
    gfx.lineBetween(X + 1, Y - 6, X + 9, Y + 6);
    gfx.lineBetween(X + 9, Y - 6, X + 1, Y + 6);
  } else {
    // Sound waves (arcs)
    gfx.lineStyle(1.5, 0x00ffff, 0.6);
    // Wave 1
    gfx.beginPath();
    gfx.arc(X - 2, Y, 6, -0.6, 0.6, false);
    gfx.strokePath();
    // Wave 2
    gfx.lineStyle(1.5, 0x00ffff, 0.35);
    gfx.beginPath();
    gfx.arc(X - 2, Y, 11, -0.5, 0.5, false);
    gfx.strokePath();
  }
}

/**
 * @param {Phaser.Scene} scene
 * @param {{ x?: number, y?: number }} [at] - icon centre; defaults to the
 *   bottom-right corner of the current canvas (desktop and mobile sizes)
 */
export function addMuteButton(scene, at = {}) {
  const X = at.x ?? scene.scale.width - INSET_X;
  const Y = at.y ?? scene.scale.height - INSET_Y;
  const gfx = scene.add.graphics();
  gfx.setDepth(1000); // always on top

  drawIcon(gfx, MusicManager.isMuted(), X, Y);

  // Invisible hit area
  const hitZone = scene.add
    .zone(X - 4, Y, SIZE + 28, SIZE + 28)
    .setOrigin(0.5)
    .setDepth(1000)
    .setInteractive({ useHandCursor: true });

  hitZone.on("pointerdown", () => {
    const nowMuted = MusicManager.toggle();
    drawIcon(gfx, nowMuted, X, Y);
  });

  return { gfx, hitZone };
}
